/**
 * MotivationalForm Widget — Orchestrator
 *
 * Questionnaire form for motivational assessment.
 * Displays in a 75%-viewport modal with a single-question stepper,
 * auto-save, inline validation, and a review step before submission.
 *
 * Questions are fetched from brain-service via the SDK
 * (`useBrainConfig('motivational_questions')`), then mapped from
 * the brain scoring shape to the UI display shape.
 *
 * Architecture:
 *  - MotivationalQuestionView: single focused question card
 *  - MotivationalReviewView: read-only summary before submit
 *  - MotivationalNavDots: navigation indicators
 *  - motivational-form.types.ts: shared types
 *  - motivational-form.utils.ts: helpers, validation, schema builder
 *
 * @module copilot/widgets/motivational-form
 */

import { cn } from '@tonnie/foundation';
import { useLocale, useTranslation } from '@tonnie/locale';
import {
  useBrainConfig,
  useSubmitMotivational,
  useSaveDraftMotivational,
  useLoadDraftMotivational,
  useRecordStandaloneTest,
} from '@tonnie/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Progress,
} from '@tonnie/ui';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Save,
  TonnieLogo,
  X,
} from '@tonnie/assets';
import { useErrorCapture } from '@tonnie/observability';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { WidgetProps } from '@tonnie/copilot';

import type {
  IBrainPersona,
  IMotivationalFormConfig,
  IQuestion,
  IValidationResult,
} from './motivational-form.types';
import { draftKey, mapBrainQuestionsToUI, validateAnswer } from './motivational-form.utils';
import { MotivationalQuestionView } from './MotivationalQuestionView';
import { MotivationalReviewView } from './MotivationalReviewView';
import { MotivationalNavDots } from './MotivationalNavDots';
import { useCheckupReuse } from '../reuse/use-checkup-reuse';
import { CheckupReusePrompt } from '../reuse/CheckupReusePrompt';

// ═══════════════════════════════════════════════════════════════════════
// HOOK — useWindowSize
// ═══════════════════════════════════════════════════════════════════════

function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const handler = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return size;
}

// ═══════════════════════════════════════════════════════════════════════
// HOOK — useAutoSave (debounced draft persistence)
// ═══════════════════════════════════════════════════════════════════════

function useAutoSave(
  answers: Record<string, string>,
  saveDraftMutation: ReturnType<typeof useSaveDraftMotivational>,
  config: IMotivationalFormConfig | undefined,
  enabled: boolean,
  captureException: (error: Error, extra?: Record<string, unknown>) => void,
  journeyInstanceId?: string
) {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // TanStack Query returns a new mutation object reference on every render.
  // Putting `saveDraftMutation` in the effect's dep array used to cancel the
  // 2-second debounce on every parent re-render — under React 19 the form's
  // parent re-renders far more aggressively (StrictMode double-invokes,
  // textarea typing, validation state churn), so the debounce timer was
  // cleared before it could fire and NO draft was ever saved. Stabilising
  // via refs keeps the effect's deps to actual user-meaningful values
  // (`answers`, `enabled`).
  const mutateRef = useRef(saveDraftMutation.mutate);
  mutateRef.current = saveDraftMutation.mutate;
  const configRef = useRef(config);
  configRef.current = config;
  const journeyInstanceIdRef = useRef(journeyInstanceId);
  journeyInstanceIdRef.current = journeyInstanceId;
  const captureExceptionRef = useRef(captureException);
  captureExceptionRef.current = captureException;
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    // Clear previous timer FIRST, before the `enabled` early-return. Submit
    // flips `enabled` false (`!isSubmitted`) the instant it succeeds, which
    // re-runs this effect — if a debounce from the last question's blur was
    // still pending, returning early without clearing it left it armed. The
    // widget then unmounts (onComplete swaps the CoPilot guide to the next
    // step) and the OTHER effect's unmount cleanup, finding that same timer
    // still set, fired one last stale draft-save — whose handler
    // unconditionally writes `status: 'DRAFT'` (by design, for a genuine
    // retake) and so silently overwrote the SUBMITTED row moments after a
    // real submission, stranding the assessment with no reusable
    // motivational evidence despite everything else being complete.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      // `clearTimeout` cancels the callback but leaves the ref holding the
      // now-dead timer id — still truthy, so the unmount-flush effect below
      // read it as "a save is still pending" and fired a stale draft-save
      // anyway. Null it out so cancelled really means cancelled.
      timerRef.current = null;
    }
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);

    if (!enabled) return;

    timerRef.current = setTimeout(() => {
      const currentAnswers = answersRef.current;
      const answersArray = Object.entries(currentAnswers)
        .filter(([, v]) => v.trim().length > 0)
        .map(([questionId, answer]) => ({ questionId, answer }));

      if (answersArray.length === 0) return;

      setSaveStatus('saving');

      mutateRef.current(
        { answers: answersArray },
        {
          onSuccess: () => {
            setSaveStatus('saved');
            fadeTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
          },
          onError: (error) => {
            captureExceptionRef.current(error instanceof Error ? error : new Error(String(error)), {
              component: 'MotivationalFormWidget',
              action: 'saveDraft',
            });
            // Fallback: save to localStorage
            try {
              const cfg = configRef.current;
              const data = {
                answers: currentAnswers,
                timestamp: new Date().toISOString(),
                config: {
                  tenant: cfg?.tenant,
                  market: cfg?.market,
                  persona: cfg?.persona,
                },
              };
              localStorage.setItem(
                draftKey(cfg?.tenant, cfg?.persona, journeyInstanceIdRef.current),
                JSON.stringify(data)
              );
              setSaveStatus('saved');
              fadeTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
            } catch {
              setSaveStatus('idle');
            }
          },
        }
      );
    }, 2000); // 2 second debounce
  }, [answers, enabled]);

  // Flush pending draft on unmount instead of cancelling it. If the user
  // navigates away inside the 2-second debounce window, fire the save
  // immediately so we don't lose what they just typed. This complements the
  // bug fix above — without it, leaving the page right after typing still
  // dropped the last edit.
  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        const pending = answersRef.current;
        const answersArray = Object.entries(pending)
          .filter(([, v]) => v.trim().length > 0)
          .map(([questionId, answer]) => ({ questionId, answer }));
        if (answersArray.length > 0) {
          mutateRef.current({ answers: answersArray });
        }
      }
    };
  }, []);

  return saveStatus;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function MotivationalFormWidget({
  config,
  onComplete,
  onCancel,
}: WidgetProps & { config?: IMotivationalFormConfig }) {
  const { t } = useTranslation();
  const { locale, formatPercent } = useLocale();
  const { captureException } = useErrorCapture();
  const { w: windowWidth, h: windowHeight } = useWindowSize();

  // talentId & journeyInstanceId are injected by CoPilotGuide from engine variables
  const talentId = (config as Record<string, unknown>)?.talentId as string | undefined;
  const journeyInstanceId = (config as Record<string, unknown>)?.journeyInstanceId as
    string | undefined;

  // BL-214: a standalone TestIt run banks its result to the reusable store
  // instead of the CheckUp assessment. Default/absent = 'checkup' (unchanged).
  const isTestit =
    ((config as Record<string, unknown>)?.submissionMode as string | undefined) === 'testit';
  // A standalone run has NO journey. The server draft endpoints scope by a uuid
  // journeyId, so we must NOT invent a non-uuid one (it 500s on the cast) — skip
  // server drafts entirely in testit mode. The localStorage draft key is
  // namespaced ('testit') so it can never collide with a real CheckUp draft.
  const serverDraftJourneyId = isTestit ? undefined : journeyInstanceId;
  const draftScopeId = isTestit ? 'testit' : journeyInstanceId;

  // BL-214: inside CheckUp, offer to reuse a valid banked motivational result.
  // The answers are hydrated into the assessment at aggregate time, so "reuse"
  // just completes the step. Read via the 'me' sentinel; never in a TestIt run.
  const isCheckup = !isTestit;
  const { reusable, isLoading: reuseLoading } = useCheckupReuse('motivational', 'me', {
    enabled: isCheckup,
  });
  const [recordNew, setRecordNew] = useState(false);

  // SDK mutations — scoped to journeyInstanceId so answers don't leak across journeys
  const submitMotivationalMutation = useSubmitMotivational(talentId ?? 'me', journeyInstanceId);
  const recordStandalone = useRecordStandaloneTest(talentId ?? 'me');
  const saveDraftMutation = useSaveDraftMotivational(talentId ?? 'me', serverDraftJourneyId);
  const { data: serverDraft } = useLoadDraftMotivational(
    talentId ?? 'me',
    serverDraftJourneyId,
    !!talentId && !isTestit
  );

  // Fetch motivational questions from brain-service
  const {
    data: brainConfig,
    isLoading: isFetchingQuestions,
    isError: isFetchError,
    refetch,
  } = useBrainConfig('motivational_questions');

  // Map brain questions to UI format, filtered by persona
  const questions: IQuestion[] = useMemo(() => {
    if (!brainConfig) return [];

    try {
      const personas = (brainConfig as { personas?: IBrainPersona[] }).personas;
      if (!personas || !Array.isArray(personas)) return [];

      const targetPersona = config?.persona || 'Default';
      const personaData =
        personas.find((p) => p.persona === targetPersona) ||
        personas.find((p) => p.persona === 'Default');

      if (!personaData?.questions?.length) return [];

      return mapBrainQuestionsToUI(personaData.questions);
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'MotivationalFormWidget',
        action: 'mapBrainQuestions',
      });
      return [];
    }
  }, [brainConfig, config?.persona]);

  // ─── State ───
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [validationResults, setValidationResults] = useState<Record<string, IValidationResult>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isReview, setIsReview] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // The modal belongs to the widget's WORK, not to its mount — identical
  // contract to `PitchRecorderWidget` (see its comment for the failure it
  // prevents). With `open` hard-coded, a host that does not unmount this widget
  // on `onComplete` left a finished form owning a full-screen overlay over the
  // whole application, with nothing on screen to explain it.
  const [isOpen, setIsOpen] = useState(true);

  /** Hand control back to the host, releasing the modal first. */
  const finish = useCallback(() => {
    setIsOpen(false);
    onComplete?.();
  }, [onComplete]);

  /** Abandon the run, releasing the modal first. */
  const abandon = useCallback(() => {
    setIsOpen(false);
    onCancel?.();
  }, [onCancel]);

  /**
   * Radix calls `onOpenChange` only for ITS OWN dismissals (Escape, overlay
   * click, close button) — never for our `setIsOpen(false)` — so this cannot
   * double-fire on the completion path.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) abandon();
    },
    [abandon]
  );

  /** Holds the post-submit dwell before control returns to the host. */
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    };
  }, []);

  // Auto-save — off for a standalone TestIt run (no journey, no server draft).
  const saveStatus = useAutoSave(
    answers,
    saveDraftMutation,
    config as IMotivationalFormConfig | undefined,
    questions.length > 0 && !isSubmitted && !isTestit,
    captureException,
    serverDraftJourneyId
  );

  // 75% viewport sizing
  const dialogWidth = windowWidth * 0.75;
  const dialogHeight = windowHeight * 0.75;

  // ─── Load draft on mount ───
  useEffect(() => {
    if (questions.length === 0) return;

    // Try server draft first
    if (serverDraft?.hasDraft && Array.isArray(serverDraft.answers)) {
      const restored: Record<string, string> = {};
      for (const item of serverDraft.answers as Array<{
        questionId: string;
        answer: string;
      }>) {
        if (item.questionId && item.answer) restored[item.questionId] = item.answer;
      }
      if (Object.keys(restored).length > 0) {
        setAnswers(restored);
        return;
      }
    }

    // Fallback to localStorage
    if (config?.tenant && config?.persona) {
      try {
        const saved = localStorage.getItem(
          draftKey(config.tenant, config.persona, isTestit ? 'testit' : undefined)
        );
        if (saved) {
          const draftData = JSON.parse(saved);
          if (draftData.answers) setAnswers(draftData.answers);
        }
      } catch (err) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'MotivationalFormWidget',
          action: 'loadDraft',
        });
      }
    }
  }, [questions.length, config?.tenant, config?.persona, serverDraft, isTestit, captureException]);

  // ─── Computed ───
  const answeredCount = Object.keys(answers).filter((k) => answers[k]?.trim().length > 0).length;
  const totalCount = questions.length;
  const progress = totalCount > 0 ? answeredCount / totalCount : 0;

  // Sets for nav dots
  const answeredSet = useMemo(() => {
    const set = new Set<number>();
    questions.forEach((q, i) => {
      if (answers[q.questionId]?.trim().length > 0) set.add(i);
    });
    return set;
  }, [questions, answers]);

  const errorSet = useMemo(() => {
    const set = new Set<number>();
    questions.forEach((q, i) => {
      if (validationResults[q.questionId]?.status === 'ERROR') set.add(i);
    });
    return set;
  }, [questions, validationResults]);

  const hasErrors = errorSet.size > 0;
  const allRequiredAnswered = questions
    .filter((q) => q.required)
    .every((q) => answers[q.questionId]?.trim().length > 0);
  const canSubmit = allRequiredAnswered && !hasErrors;

  // ─── Handlers ───
  const handleAnswerChange = useCallback(
    (value: string) => {
      if (isSubmitted) return;
      const qId = questions[currentIndex]?.questionId;
      if (!qId) return;
      setAnswers((prev) => ({ ...prev, [qId]: value }));
    },
    [questions, currentIndex, isSubmitted]
  );

  const handleValidateQuestion = useCallback(
    (result: IValidationResult | undefined) => {
      const qId = questions[currentIndex]?.questionId;
      if (!qId) return;
      setValidationResults((prev) => {
        if (!result) {
          const next = { ...prev };
          delete next[qId];
          return next;
        }
        return { ...prev, [qId]: result };
      });
    },
    [questions, currentIndex]
  );

  const handleNavigate = useCallback(
    (index: number) => {
      if (isSubmitted) return;

      // Validate current question before leaving
      if (!isReview && questions[currentIndex]) {
        const q = questions[currentIndex];
        const result = validateAnswer(answers[q.questionId] || '', {
          required: q.required,
          maxChars: q.maxLength,
        });
        if (result) {
          setValidationResults((prev) => ({
            ...prev,
            [q.questionId]: result,
          }));
        }
      }

      if (index === -1) {
        // Go to review
        setIsReview(true);
        setCurrentIndex(0);
      } else {
        setIsReview(false);
        setCurrentIndex(Math.max(0, Math.min(index, questions.length - 1)));
      }
    },
    [isReview, isSubmitted, questions, currentIndex, answers]
  );

  const handleContinue = useCallback(() => {
    if (isReview) return;

    // Validate current before moving forward
    const q = questions[currentIndex];
    if (q) {
      const result = validateAnswer(answers[q.questionId] || '', {
        required: q.required,
        maxChars: q.maxLength,
      });
      if (result) {
        setValidationResults((prev) => ({ ...prev, [q.questionId]: result }));
      }
      // Block navigation on error
      if (result?.status === 'ERROR') return;
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      // Last question → go to review
      // Validate all questions first
      const allResults: Record<string, IValidationResult> = {};
      questions.forEach((q) => {
        const result = validateAnswer(answers[q.questionId] || '', {
          required: q.required,
          maxChars: q.maxLength,
        });
        if (result) allResults[q.questionId] = result;
      });
      setValidationResults(allResults);
      setIsReview(true);
    }
  }, [isReview, currentIndex, questions, answers]);

  const handleBack = useCallback(() => {
    if (isReview) {
      setIsReview(false);
      setCurrentIndex(questions.length - 1);
    } else if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  }, [isReview, currentIndex, questions.length]);

  const handleSubmit = useCallback(async () => {
    // Final validation
    const allResults: Record<string, IValidationResult> = {};
    let hasBlockingErrors = false;
    questions.forEach((q) => {
      const result = validateAnswer(answers[q.questionId] || '', {
        required: q.required,
        maxChars: q.maxLength,
      });
      if (result) {
        allResults[q.questionId] = result;
        if (result.status === 'ERROR') hasBlockingErrors = true;
      }
    });
    setValidationResults(allResults);

    if (hasBlockingErrors || !allRequiredAnswered) {
      toast.error(t('copilot.fixErrors'));
      return;
    }

    setIsSubmitted(true);

    const apiPayload = {
      answers: questions
        .filter((q) => answers[q.questionId]?.trim())
        .map((q) => ({
          questionId: q.questionId,
          answer: answers[q.questionId].trim(),
        })),
    };

    try {
      if (isTestit) {
        // Standalone TestIt run — bank the reusable result instead of feeding a
        // CheckUp assessment (source `testit`; catalog-computed expiry). The full
        // answers + author locale ride in the payload so a later CheckUp can
        // project them onto its journey and actually score them (backend
        // `hydrateStandaloneMotivational`).
        await recordStandalone.mutateAsync({
          testId: 'motivational',
          result: {
            questionsAnswered: apiPayload.answers.length,
            answers: apiPayload.answers,
            sourceLocale: locale,
          },
          score: null,
        });
      } else {
        await submitMotivationalMutation.mutateAsync(apiPayload);
      }
      toast.success(t('copilot.answersSubmitted'));
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'MotivationalFormWidget',
        action: 'submit',
      });
      toast.error(t('copilot.submitFailed'));
      setIsSubmitted(false);
      return;
    }

    // Clear draft
    try {
      localStorage.removeItem(draftKey(config?.tenant, config?.persona, draftScopeId));
    } catch {
      /* ignore */
    }

    // Release the modal AND hand control back in the same beat — `finish` does
    // both, so the overlay never outlives the work it was opened for.
    completeTimerRef.current = setTimeout(finish, 500);
  }, [
    questions,
    answers,
    allRequiredAnswered,
    config,
    finish,
    submitMotivationalMutation,
    recordStandalone,
    isTestit,
    draftScopeId,
    locale,
    t,
    captureException,
  ]);

  // ─── Reuse gate (CheckUp only) ───
  // Banked answers are hydrated into the assessment at aggregate time, so
  // "reuse" just completes the step. "Take a new one" falls through to the form.
  if (isCheckup && !recordNew && (reuseLoading || reusable)) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="flex flex-col items-center justify-center gap-6 p-6 !max-w-none"
          style={{ width: `${dialogWidth}px`, height: `${dialogHeight}px` }}
          closeLabel={t('common.close')}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{t('checkup.reuse.title')}</DialogTitle>
            <DialogDescription>{t('checkup.reuse.subtitle')}</DialogDescription>
          </DialogHeader>
          {reuseLoading || !reusable ? (
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          ) : (
            <CheckupReusePrompt
              result={reusable}
              onReuse={finish}
              onRecordNew={() => setRecordNew(true)}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Render: Error state ───
  if (isFetchError) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="flex flex-col items-center justify-center gap-6 p-6 !max-w-none"
          style={{ width: `${dialogWidth}px`, height: `${dialogHeight}px` }}
          closeLabel={t('common.close')}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{t('copilot.errorTitle')}</DialogTitle>
            <DialogDescription>{t('copilot.questionsLoadError')}</DialogDescription>
          </DialogHeader>
          <div className="w-full max-w-md p-4 border border-destructive/50 bg-destructive/10 rounded-lg flex gap-3">
            <AlertCircle className="h-5 w-5 text-destructive-accessible shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1 text-base text-foreground">
                {t('copilot.errorLoadingQuestions')}
              </div>
              <div className="text-sm text-muted-foreground">{t('copilot.questionsLoadError')}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('common.retry')}
            </Button>
            <Button onClick={abandon} variant="ghost" className="rounded-full px-6">
              {t('common.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Current question
  const currentQuestion = questions[currentIndex];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="p-0 flex flex-col rounded-xl !max-w-none border-border/50 shadow-lg"
        style={{
          width: `${dialogWidth}px`,
          height: `${dialogHeight}px`,
          maxWidth: 'none',
          maxHeight: 'none',
        }}
        data-testid="widget-motivational_form"
      >
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={abandon}
          className="absolute top-4 right-4 z-50 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background border border-border/50 shadow-sm"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </Button>

        {/* ─── Header ─── */}
        <DialogHeader className="px-6 py-4 border-b border-border/50 bg-linear-to-r from-card to-card/95 pr-16">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Logo */}
              <div className="w-9 h-9 flex items-center justify-center shrink-0">
                <TonnieLogo variant="symbol" size="sm" />
              </div>

              {/* Title + subtitle */}
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-bold text-foreground">
                  {config?.ui?.title ?? t('copilot.motivationalAssessment')}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {config?.ui?.subtitle ?? t('copilot.motivationalDesc')}
                </DialogDescription>
              </div>
            </div>

            {/* Auto-save indicator */}
            <AnimatePresence mode="wait">
              {saveStatus !== 'idle' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex items-center gap-1.5 shrink-0"
                >
                  {saveStatus === 'saving' ? (
                    <span data-testid="widget-motivational_form-save-draft">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground inline mr-1.5" />
                      <span className="text-xs text-muted-foreground">{t('copilot.saving')}</span>
                    </span>
                  ) : (
                    <span data-testid="widget-motivational_form-save-draft">
                      <Save className="w-3.5 h-3.5 text-success-accessible inline mr-1.5" />
                      <span className="text-xs text-success-accessible font-medium">
                        {t('copilot.saved')}
                      </span>
                    </span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {answeredCount} / {totalCount} {t('copilot.answered')}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatPercent(Math.round(progress * 100))}
              </span>
            </div>
            <Progress aria-label={t('common.progress')} value={progress * 100} className="h-1.5" />
          </div>

          {/* Navigation dots */}
          {questions.length > 0 && (
            <MotivationalNavDots
              total={totalCount}
              currentIndex={currentIndex}
              answeredSet={answeredSet}
              errorSet={errorSet}
              isReview={isReview}
              onNavigate={handleNavigate}
            />
          )}
        </DialogHeader>

        {/* ─── Body ─── */}
        {/*
          When showing a single question: no body scroll — textarea scrolls internally.
          When showing review/loading/empty: body scrolls to accommodate long content.
        */}
        <div
          className={cn(
            'flex-1 min-h-0 bg-background p-5 flex flex-col',
            isReview || isFetchingQuestions || questions.length === 0
              ? 'overflow-y-auto'
              : 'overflow-hidden'
          )}
        >
          {isFetchingQuestions ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12 flex-1">
              <Loader2 className="w-8 h-8 animate-spin text-primary-accessible" />
              <span className="text-sm text-muted-foreground">{t('copilot.loadingQuestions')}</span>
            </div>
          ) : questions.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12 text-center flex-1">
              <AlertCircle className="w-10 h-10 text-muted-foreground/50" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {t('copilot.questionsUnavailable')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('copilot.noQuestionsForPersona')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="rounded-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.retry')}
              </Button>
            </div>
          ) : isReview ? (
            <MotivationalReviewView
              questions={questions}
              answers={answers}
              validationResults={validationResults}
              onEditQuestion={(index) => handleNavigate(index)}
            />
          ) : currentQuestion ? (
            <AnimatePresence mode="wait">
              <MotivationalQuestionView
                key={currentQuestion.questionId}
                question={currentQuestion}
                index={currentIndex}
                total={totalCount}
                answer={answers[currentQuestion.questionId] || ''}
                onAnswerChange={handleAnswerChange}
                validationResult={validationResults[currentQuestion.questionId]}
                onValidate={handleValidateQuestion}
              />
            </AnimatePresence>
          ) : null}
        </div>

        {/* ─── Footer ─── */}
        {isSubmitted ? (
          <div
            className="px-6 py-4 bg-success/10 border-t border-success/30 flex items-center gap-3"
            data-testid="widget-motivational_form-complete"
          >
            <CheckCircle2 className="w-4 h-4 text-success-accessible shrink-0" />
            <span className="text-xs text-success-accessible flex-1">
              {t('copilot.formSubmittedNote')}
            </span>
            <Button onClick={finish} size="sm" className="h-9 px-4 rounded-full">
              {t('common.close')}
            </Button>
          </div>
        ) : questions.length > 0 ? (
          <div className="px-6 py-4 border-t border-border/50 flex items-center justify-between gap-4">
            {/* Left: Back / Cancel */}
            <div>
              {currentIndex > 0 || isReview ? (
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  size="sm"
                  className="h-9 px-4 rounded-full gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('common.back')}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={abandon}
                  size="sm"
                  className="h-9 px-4 rounded-full text-muted-foreground"
                >
                  {t('common.cancel')}
                </Button>
              )}
            </div>

            {/* Right: Continue / Review / Submit */}
            <div>
              {isReview ? (
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitMotivationalMutation.isPending}
                  size="sm"
                  className="h-9 px-6 rounded-full gap-2"
                  data-testid="widget-motivational_form-submit"
                >
                  {submitMotivationalMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  {t('common.submit')}
                </Button>
              ) : currentIndex === questions.length - 1 ? (
                <Button onClick={handleContinue} size="sm" className="h-9 px-5 rounded-full gap-2">
                  {t('copilot.reviewAnswers')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button onClick={handleContinue} size="sm" className="h-9 px-5 rounded-full gap-2">
                  {t('common.continue')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
